import { useEffect, useState } from "react";
import { toast, Toaster } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { Save, Plus, Trash2, Image as ImageIcon, GripVertical } from "lucide-react";
import MediaLibraryModal from "@/components/editor/MediaLibraryModal";
import {
  DndContext,
  closestCenter,
  PointerSensor,
  KeyboardSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  SortableContext,
  arrayMove,
  verticalListSortingStrategy,
  useSortable,
  sortableKeyboardCoordinates,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

const blank = () => ({ uid: crypto.randomUUID(), name: "", image: "" });
const withUid = (it) => (it && it.uid ? it : { ...it, uid: crypto.randomUUID() });

const AdminOutlets = () => {
  const [data, setData] = useState({ heading_ar: "", heading_en: "", items: [] });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [pickerFor, setPickerFor] = useState(null);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  useEffect(() => {
    api
      .get("/admin/content/outlets")
      .then((r) => {
        const d = r.data?.data || {};
        setData({
          heading_ar: d.heading_ar || "",
          heading_en: d.heading_en || "",
          items: (d.items || []).map(withUid),
        });
      })
      .catch((e) => toast.error(formatApiError(e)))
      .finally(() => setLoading(false));
  }, []);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        heading_ar: data.heading_ar,
        heading_en: data.heading_en,
        items: data.items.map(({ uid, ...rest }) => rest),
      };
      await api.put("/admin/content/outlets", payload);
      toast.success("تم حفظ الجهات بالترتيب الجديد");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const setItem = (uid, key, v) => setData((d) => ({
    ...d,
    items: d.items.map((it) => (it.uid === uid ? { ...it, [key]: v } : it)),
  }));
  const addItem = () => setData((d) => ({ ...d, items: [...d.items, blank()] }));
  const delItem = (uid) => setData((d) => ({ ...d, items: d.items.filter((it) => it.uid !== uid) }));

  const onDragEnd = ({ active, over }) => {
    if (!over || active.id === over.id) return;
    setData((d) => {
      const oldIdx = d.items.findIndex((it) => it.uid === active.id);
      const newIdx = d.items.findIndex((it) => it.uid === over.id);
      return { ...d, items: arrayMove(d.items, oldIdx, newIdx) };
    });
  };

  if (loading) return <div className="overline animate-pulse">…</div>;

  return (
    <div data-testid="admin-outlets">
      <Toaster position="top-left" theme="dark" richColors />
      <div className="overline mb-3">محتوى الصفحة</div>
      <h1 className="font-arabic font-black text-3xl sm:text-4xl text-bone mb-4">أبرز الجهات الإعلامية</h1>
      <p className="text-white/50 text-sm mb-8 font-arabic">اسحب وأفلِت لإعادة ترتيب الشعارات.</p>

      <form onSubmit={save} className="space-y-6 max-w-5xl">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <In label="العنوان (عربي)" v={data.heading_ar} on={(v) => setData((d) => ({ ...d, heading_ar: v }))} testId="outlets-heading-ar" />
          <In label="Heading (English)" v={data.heading_en} on={(v) => setData((d) => ({ ...d, heading_en: v }))} ltr testId="outlets-heading-en" />
        </div>

        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
          <SortableContext items={data.items.map((it) => it.uid)} strategy={verticalListSortingStrategy}>
            <div className="space-y-3">
              {data.items.map((it, i) => (
                <SortableOutlet
                  key={it.uid}
                  item={it}
                  index={i}
                  setItem={setItem}
                  delItem={delItem}
                  openPicker={() => setPickerFor(it.uid)}
                />
              ))}
            </div>
          </SortableContext>
        </DndContext>

        <button type="button" onClick={addItem} className="inline-flex items-center gap-2 overline text-white/65 hover:text-gold" data-testid="outlets-add">
          <Plus size={12} /> إضافة جهة
        </button>

        <div>
          <button type="submit" disabled={saving} className="btn-gold disabled:opacity-60" data-testid="outlets-save">
            <Save size={14} />
            {saving ? "جاري الحفظ…" : "حفظ"}
          </button>
        </div>
      </form>

      <MediaLibraryModal
        open={pickerFor !== null}
        kind="image"
        onClose={() => setPickerFor(null)}
        onSelect={(media) => {
          setItem(pickerFor, "image", media.url);
          setPickerFor(null);
        }}
      />
    </div>
  );
};

const SortableOutlet = ({ item, index, setItem, delItem, openPicker }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: item.uid });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.7 : 1,
  };
  return (
    <div
      ref={setNodeRef}
      style={style}
      data-testid={`outlet-row-${index}`}
      className="grid grid-cols-1 md:grid-cols-[36px_120px_1fr_1fr_auto_auto] gap-3 items-center border border-white/10 bg-ink-soft p-4"
    >
      <button
        type="button"
        className="flex items-center justify-center h-10 w-9 text-white/40 hover:text-gold cursor-grab active:cursor-grabbing touch-none"
        aria-label="drag"
        {...attributes}
        {...listeners}
        data-testid={`outlet-drag-${index}`}
      >
        <GripVertical size={16} />
      </button>
      <div className="h-20 flex items-center justify-center bg-ink border border-white/10 p-2">
        {item.image ? (
          <img src={item.image} alt="" className="max-h-full max-w-full object-contain" />
        ) : (
          <ImageIcon size={20} className="text-white/30" />
        )}
      </div>
      <In label="الاسم" v={item.name} on={(v) => setItem(item.uid, "name", v)} />
      <In label="رابط الشعار" v={item.image} on={(v) => setItem(item.uid, "image", v)} ltr />
      <button
        type="button"
        onClick={openPicker}
        className="inline-flex items-center gap-2 border border-white/15 hover:border-gold hover:text-gold text-xs tracking-[0.15em] uppercase px-3 py-2"
        data-testid={`outlet-library-${index}`}
      >
        <ImageIcon size={12} /> المكتبة
      </button>
      <button type="button" onClick={() => delItem(item.uid)} className="text-white/50 hover:text-red-400" data-testid={`outlet-delete-${index}`}>
        <Trash2 size={14} />
      </button>
    </div>
  );
};

const In = ({ label, v, on, ltr, testId }) => (
  <label className="block">
    <span className="block mb-2 text-xs tracking-[0.2em] uppercase text-white/50 font-arabic">{label}</span>
    <input
      type="text"
      value={v || ""}
      onChange={(e) => on(e.target.value)}
      className="editorial-input"
      dir={ltr ? "ltr" : "auto"}
      data-testid={testId}
    />
  </label>
);

export default AdminOutlets;
